import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TbCheck, TbUsers } from "react-icons/tb";
import type { BookingDetailResult, CustomerName } from "src/store/apis/Hotels";

const GuestField: FC<{ title: ReactNode; value: ReactNode }> = ({ title, value }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-1">
    <div className="text-font-subtle text-xs uppercase">{title}</div>
    <div className="text-font-default truncate text-sm">{value}</div>
  </div>
);

const GuestCard: FC<{ guest: CustomerName; index: number; isPrimary: boolean }> = ({ guest, index, isPrimary }) => {
  const { t } = useTranslation();

  return (
    <div className="border-border-subtle overflow-hidden rounded-sm border">
      <div className="bg-background-subtle border-border-subtle flex items-center justify-between gap-4 border-b px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="bg-background-primary-lighter text-font-active flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold uppercase">
            {guest.firstName?.[0]}
          </div>
          <span className="text-font-subtle text-xs uppercase">{t("BookingResult.guestDetails.guestNumber", { number: index + 1 })}</span>
          <span className="text-font-default truncate text-sm font-semibold">
            {[guest.title, guest.firstName, guest.lastName].filter(Boolean).join(" ")}
          </span>
        </div>
        {isPrimary && (
          <span className="bg-background-primary text-font-inverted rounded-pill flex shrink-0 items-center gap-1 px-3 py-1 text-xs font-medium">
            <TbCheck className="size-3.5" />
            {t("BookingResult.guestDetails.primaryContact")}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-4 p-4 md:flex-row">
        <GuestField title={t("BookingResult.guestDetails.title")} value={guest.title} />
        <GuestField title={t("BookingResult.guestDetails.firstName")} value={guest.firstName} />
        <GuestField title={t("BookingResult.guestDetails.lastName")} value={guest.lastName} />
      </div>
    </div>
  );
};

const GuestDetails: FC<{ bookingDetail: BookingDetailResult }> = ({ bookingDetail }) => {
  const { t } = useTranslation();
  const guests = bookingDetail.rooms.flatMap((room) => room.customerDetails.flatMap((detail) => detail.customerNames ?? []));

  return (
    <div className="bg-background-default border-border-subtle flex w-full flex-col overflow-hidden rounded-sm border shadow-xs">
      <div className="bg-background-subtle border-border-subtle flex items-center gap-2 border-b px-4 py-3">
        <TbUsers className="text-font-active size-5" />
        <div className="text-font-default text-base font-semibold">{t("BookingResult.guestDetails.sectionTitle")}</div>
      </div>
      <div className="flex flex-col gap-3 p-4">
        {guests.map((guest, index) => (
          <GuestCard key={index} guest={guest} index={index} isPrimary={index === 0} />
        ))}
      </div>
    </div>
  );
};

export default GuestDetails;
